// Use centralized API client for proper authentication handling
import { ticketsApi } from './apiClient';

/**
 * Service for handling ticket-related API interactions with caching
 * Note: Tickets change frequently, so cache TTL is shorter (2 minutes)
 */
class TicketService {
  /**
   * Cache storage
   * @private
   */
  static cache = {
    tickets: null,
    statistics: null,
    timestamp: null,
    ttl: 2 * 60 * 1000, // 2 minutes cache TTL (shorter for tickets)
  };

  /**
   * Check if cache is valid
   * @private
   * @returns {boolean} True if cache is valid
   */
  static isCacheValid() {
    if (!this.cache.tickets || !this.cache.timestamp) {
      return false;
    }
    const now = Date.now();
    return (now - this.cache.timestamp) < this.cache.ttl;
  }

  /**
   * Invalidate ticket cache
   */
  static invalidateCache() {
    console.log('[TicketService] Cache invalidated');
    this.cache.tickets = null;
    this.cache.statistics = null;
    this.cache.timestamp = null;
  }

  /**
   * Get all tickets with optional filtering and caching
   * @param {Object} options - Options object
   * @param {Object} options.params - Query parameters for filtering tickets
   * @param {boolean} options.useCache - Whether to use cache (default: false for tickets)
   * @param {boolean} options.forceRefresh - Force refresh even if cache is valid
   * @returns {Promise<Object>} Response object with tickets data
   */
  static async getAllTickets({ params = {}, useCache = false, forceRefresh = false } = {}) {
    try {
      // Return cached data if available and valid
      // Note: useCache defaults to false for tickets since they change frequently
      if (useCache && !forceRefresh && TicketService.isCacheValid()) {
        console.log('[TicketService] Returning cached tickets');
        return TicketService.cache.tickets;
      }

      console.log('[TicketService] Fetching tickets from API');

      const response = await ticketsApi.getAll(params);

      console.log('[TicketService] Raw API response:', response);

      // Cache the entire response object to maintain structure
      TicketService.cache.tickets = response;
      TicketService.cache.timestamp = Date.now();

      return response;
    } catch (error) {
      console.error('[TicketService] getAllTickets error:', error);
      throw error;
    }
  }

  /**
   * Get ticket statistics (for dashboard)
   * @param {Object} options - Options object
   * @param {boolean} options.useCache - Whether to use cache (default: true)
   * @returns {Promise<Object>} Ticket statistics
   */
  static async getStatistics({ useCache = true } = {}) {
    try {
      // If we have cached statistics and cache is valid, return it
      if (useCache && TicketService.isCacheValid() && TicketService.cache.statistics) {
        console.log('[TicketService] Returning cached statistics');
        return TicketService.cache.statistics;
      }

      console.log('[TicketService] Fetching statistics from API');
      const response = await ticketsApi.getStatistics();

      // Cache the statistics separately
      TicketService.cache.statistics = response;
      if (!TicketService.cache.timestamp) {
        TicketService.cache.timestamp = Date.now();
      }

      return response;
    } catch (error) {
      console.error('[TicketService] getStatistics error:', error);
      // Return default structure on error
      return {
        data: {
          total: 0,
          open: 0,
          inProgress: 0,
          closed: 0
        }
      };
    }
  }

  /**
   * Get a specific ticket by ID
   * @param {string} ticketId - Ticket identifier
   * @returns {Promise<Object>} Ticket object
   */
  static async getTicketById(ticketId) {
    try {
      const response = await ticketsApi.getById(ticketId);
      return response;
    } catch (error) {
      console.error(`[TicketService] getTicketById(${ticketId}) error:`, error);
      throw error;
    }
  }

  /**
   * Create a new ticket
   * @param {Object} ticketData - Ticket creation payload
   * @returns {Promise<Object>} Created ticket object
   */
  static async createTicket(ticketData) {
    try {
      const response = await ticketsApi.create(ticketData);
      // Invalidate cache since ticket list changed
      TicketService.invalidateCache();
      return response;
    } catch (error) {
      console.error('[TicketService] createTicket error:', error);
      throw error;
    }
  }

  /**
   * Update an existing ticket
   * @param {string} ticketId - Ticket identifier
   * @param {Object} updateData - Ticket update payload
   * @returns {Promise<Object>} Updated ticket object
   */
  static async updateTicket(ticketId, updateData) {
    try {
      const response = await ticketsApi.update(ticketId, updateData);
      // Invalidate cache since ticket changed
      TicketService.invalidateCache();
      return response;
    } catch (error) {
      console.error(`[TicketService] updateTicket(${ticketId}) error:`, error);
      throw error;
    }
  }

  /**
   * Delete a ticket
   * @param {string} ticketId - Ticket identifier
   * @returns {Promise<void>}
   */
  static async deleteTicket(ticketId) {
    try {
      await ticketsApi.delete(ticketId);
      // Invalidate cache since ticket list changed
      TicketService.invalidateCache();
    } catch (error) {
      console.error(`[TicketService] deleteTicket(${ticketId}) error:`, error);
      throw error;
    }
  }

  /**
   * Add a comment to a ticket
   * @param {string} ticketId - Ticket identifier
   * @param {Object} commentData - Comment data
   * @returns {Promise<Object>} Created comment object
   */
  static async addComment(ticketId, commentData) {
    try {
      const response = await ticketsApi.addComment(ticketId, commentData);
      // Invalidate cache since ticket changed
      TicketService.invalidateCache();
      return response;
    } catch (error) {
      console.error(`[TicketService] addComment(${ticketId}) error:`, error);
      throw error;
    }
  }

  /**
   * Change ticket status
   * @param {string} ticketId - Ticket identifier
   * @param {string} status - New status
   * @returns {Promise<Object>} Updated ticket object
   */
  static async changeStatus(ticketId, status) {
    try {
      const response = await ticketsApi.changeStatus(ticketId, status);
      // Invalidate cache since ticket changed
      TicketService.invalidateCache();
      return response;
    } catch (error) {
      console.error(`[TicketService] changeStatus(${ticketId}) error:`, error);
      throw error;
    }
  }

  /**
   * Assign ticket to a user
   * @param {string} ticketId - Ticket identifier
   * @param {string} userId - User ID to assign to
   * @returns {Promise<Object>} Updated ticket object
   */
  static async assignTicket(ticketId, userId) {
    try {
      const response = await ticketsApi.assignTicket(ticketId, userId);
      // Invalidate cache since ticket changed
      TicketService.invalidateCache();
      return response;
    } catch (error) {
      console.error(`[TicketService] assignTicket(${ticketId}) error:`, error);
      throw error;
    }
  }
}

export default TicketService;
